import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ISaveTypePrescription } from "@/models/doctor";
import {
  IModalKeyEnums,
  IPrescriptionPermissionEnums,
} from "@/models/permission";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setIsVideoCallChatInitiated,
  setIsVideoCallPrescriptionInitiated,
  setIsVideoMinimizedOrPrescInitiated,
  setIsVideoUnMinimized,
  videoAppointmentDetails,
} from "@/store/slices/videoAppointment";
import { setPrescriptionTemplateValues } from "@/store/slices/doctor/prescription";
import { setAppointmentDetail } from "@/store/slices/doctor/appointment";
import { fetchScheduleAppointmentList } from "@/store/slices/doctor/schedule";

import {
  createPrescriptionApi,
  saveMedicalRecordApi,
  updatePrescriptionApi,
} from "@/utils/apis/doctor/prescription";
import { filteredNullEmptyArrayData } from "@/utils";
import { INotifyEnum, notify } from "@/utils/toaster";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";

const usePrescriptionCreate = () => {
  const [isSavePrescriptionLoading, setIsSavePrescriptionLoading] =
    useState<boolean>(false);
  const [isEndPrescriptionLoading, setIsEndPrescriptionLoading] =
    useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [showSavePrescription, setShowSavePrescription] =
    useState<boolean>(false);
  const [sharePrescription, setSharePrescription] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string>("");

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const prescription = useAppSelector((state) => state.prescription);
  const appointmentDetail = useAppSelector(
    (state) => state.appointment.appointmentDetail
  );
  const {
    isVideoCallInitiated,
    appointmentDetail: videoAppointmentDetail,
    activePrescription,
  } = useAppSelector(videoAppointmentDetails);
  const { selectedDate } = useAppSelector((state) => state.schedule);
  const permissionRestrictionHandler = usePermissionRestriction();

  const openCloseSharePrescriptionModal = () =>
    setSharePrescription(!sharePrescription);

  const openCloseSavePrescriptionModal = () =>
    setShowSavePrescription(!showSavePrescription);

  const handleCreatePrescriptionHandler = async (
    saveTypePrescription: ISaveTypePrescription,
    execute: boolean
  ) => {
    if (
      saveTypePrescription === ISaveTypePrescription.SAVE_PRESCRIPTION &&
      !showSavePrescription
    ) {
      openCloseSavePrescriptionModal();
      return;
    }
    if (saveTypePrescription === ISaveTypePrescription.SAVE_PRESCRIPTION) {
      setIsSavePrescriptionLoading(true);
    } else {
      setIsEndPrescriptionLoading(true);
    }
    const medicine = prescription?.create?.medicine?.map(({ id, ...rest }) => ({
      ...rest,
    }));
    const test = prescription?.create?.test?.map(({ id, ...rest }) => ({
      ...rest,
    }));

    if (
      isVideoCallInitiated
        ? activePrescription
        : appointmentDetail?.activePrescription
    ) {
      const isPermissionGranted = permissionRestrictionHandler(
        IModalKeyEnums.PRESCRIPTION,
        IPrescriptionPermissionEnums.UPDATE_PRESCRIPTION
      );
      if (!isPermissionGranted) return;
      const isPermissionGrantedForMedicalRecord = permissionRestrictionHandler(
        IModalKeyEnums.PRESCRIPTION,
        IPrescriptionPermissionEnums.SAVE_PRESCRIPTION_MEDICAL_RECORD
      );
      if (!isPermissionGrantedForMedicalRecord) return;
      const {
        id,
        doctorId,
        memberId,
        patientId,
        bookingId,
        prescription_id,
        ...updatedPrescription
      } = prescription?.create || {};
      const prescriptionPayload =
        filteredNullEmptyArrayData(updatedPrescription);
      const updatePrescriptionResponse = await updatePrescriptionApi(
        prescription?.create?.id!,
        {
          ...prescriptionPayload,
          medicine,
          test,
          saveType: saveTypePrescription,
          nextVisitNo:
            updatedPrescription?.nextVisitNo?.length == 0
              ? 0
              : +updatedPrescription?.nextVisitNo,
        }
      );
      if ("result" in updatePrescriptionResponse) {
        notify(INotifyEnum.SUCCESS, updatePrescriptionResponse?.message);
        if (saveTypePrescription === ISaveTypePrescription.SAVE_PRESCRIPTION) {
          setIsSaved(true);
          openCloseSavePrescriptionModal();
          if (!isVideoCallInitiated) {
            dispatch(
              fetchScheduleAppointmentList(
                selectedDate?.startDate
                  ? {
                      start: selectedDate.startDate,
                      end: selectedDate.endDate,
                    }
                  : {}
              )
            );
            navigate("/schedule");
          } else {
            dispatch(setIsVideoMinimizedOrPrescInitiated({ value: false }));
            dispatch(setIsVideoCallPrescriptionInitiated({ value: false }));
          }
        } else {
          openCloseSharePrescriptionModal();
          setPdfUrl(updatePrescriptionResponse?.result?.url!);
        }
      } else {
        notify(INotifyEnum.ERROR, updatePrescriptionResponse?.message);
        openCloseSavePrescriptionModal();
        dispatch(setIsVideoUnMinimized({ value: true }));
        setIsSaved(false);
      }
      setIsSavePrescriptionLoading(false);
      setIsEndPrescriptionLoading(false);
    } else {
      const isPermissionGranted = permissionRestrictionHandler(
        IModalKeyEnums.PRESCRIPTION,
        IPrescriptionPermissionEnums.SAVE_PRESCRIPTION_MEDICAL_RECORD
      );
      if (!isPermissionGranted) return;

      if (
        saveTypePrescription === ISaveTypePrescription.SAVE_PRESCRIPTION &&
        !execute
      ) {
        openCloseSavePrescriptionModal();
        return;
      }

      const { id, doctorId, patientId, memberId, ...prescriptionData } =
        prescription?.create;
      const prescriptionDataPayload =
        filteredNullEmptyArrayData(prescriptionData);
      const createPrescriptionResponse = await createPrescriptionApi({
        ...prescriptionDataPayload,
        medicine,
        test,
        saveType: saveTypePrescription,
        bookingId: isVideoCallInitiated
          ? videoAppointmentDetail?.id
          : appointmentDetail?.id,
        nextVisitNo:
          prescriptionData?.nextVisitNo?.length == 0
            ? 0
            : +prescriptionData?.nextVisitNo,
      });
      if ("result" in createPrescriptionResponse) {
        notify(INotifyEnum.SUCCESS, createPrescriptionResponse?.message);

        if (saveTypePrescription === ISaveTypePrescription.SAVE_PRESCRIPTION) {
          setIsSaved(true);
          openCloseSavePrescriptionModal();
          if (!isVideoCallInitiated) {
            dispatch(
              fetchScheduleAppointmentList(
                selectedDate?.startDate
                  ? {
                      start: selectedDate.startDate,
                      end: selectedDate.endDate,
                    }
                  : {}
              )
            );
            navigate("/schedule");
          } else {
            dispatch(setIsVideoUnMinimized({ value: true }));
            dispatch(setIsVideoMinimizedOrPrescInitiated({ value: false }));
            dispatch(setIsVideoCallPrescriptionInitiated({ value: false }));
            dispatch(setIsVideoCallChatInitiated({ value: false }));
          }
        } else {
          openCloseSharePrescriptionModal();
          setPdfUrl(createPrescriptionResponse?.result?.url!);
        }
      } else {
        notify(INotifyEnum.ERROR, createPrescriptionResponse?.message);
        setIsSaved(false);
        setIsSavePrescriptionLoading(false);
        setIsEndPrescriptionLoading(false);
        setShowSavePrescription(false);
        dispatch(setIsVideoUnMinimized({ value: true }));
        return;
      }
      setIsSavePrescriptionLoading(false);
      setIsEndPrescriptionLoading(false);
    }
    dispatch(setPrescriptionTemplateValues({}));
    if (saveTypePrescription === ISaveTypePrescription.SAVE_PRESCRIPTION) {
      dispatch(setAppointmentDetail({}));
      dispatch(setIsVideoCallPrescriptionInitiated({ value: false }));
      // dispatch(setVideoAppointmentDetail({}));
    }
  };

  const createPrescriptionByUploadHandler = async (
    healthCategoryId: string,
    url: string,
    handleClose?: () => void
  ) => {
    setIsEndPrescriptionLoading(true);
    const createPrescriptionResponse = await saveMedicalRecordApi({
      healthCategoryId,
      url,
      bookingId: isVideoCallInitiated
        ? videoAppointmentDetail?.id
        : appointmentDetail?.id,
    });
    if ("result" in createPrescriptionResponse) {
      setPdfUrl(createPrescriptionResponse?.result?.url);
      openCloseSharePrescriptionModal();
      handleClose && handleClose();
      notify(INotifyEnum.SUCCESS, createPrescriptionResponse?.message);
      if (isVideoCallInitiated) {
        dispatch(setIsVideoUnMinimized({ value: true }));
        dispatch(setIsVideoMinimizedOrPrescInitiated({ value: false }));
        dispatch(setIsVideoCallPrescriptionInitiated({ value: false }));
        dispatch(setIsVideoCallChatInitiated({ value: false }));
      }
    } else {
      notify(INotifyEnum.ERROR, createPrescriptionResponse?.message);
    }
    setIsEndPrescriptionLoading(false);
  };

  return {
    isSavePrescriptionLoading,
    isEndPrescriptionLoading,
    pdfUrl,
    sharePrescription,
    showSavePrescription,
    openCloseSavePrescriptionModal,
    handleCreatePrescriptionHandler,
    openCloseSharePrescriptionModal,
    createPrescriptionByUploadHandler,
    isSaved,
  };
};

export default usePrescriptionCreate;
